import type { MapPoint } from '@london-life/game/city/types';

// The way between two places on the map, along the streets: the streets are cut where they cross, the start
// and the end join the nearest street, and the shortest way is found. Streets that do not meet (the river
// splits the city) are joined where they come closest, as the tunnels and bridges do.

type Edge = { to: number; length: number };
export type RoadGraph = { nodes: MapPoint[]; edges: Edge[][] };

const distance = (a: MapPoint, b: MapPoint) => Math.hypot(a[0] - b[0], a[1] - b[1]);
// where two segments cross (as a fraction along each), if they do
function crossing(a: MapPoint, b: MapPoint, c: MapPoint, d: MapPoint) {
  const r = [b[0] - a[0], b[1] - a[1]],
    s = [d[0] - c[0], d[1] - c[1]];
  const denominator = r[0] * s[1] - r[1] * s[0];
  if (Math.abs(denominator) < 1e-9) return null;
  const t = ((c[0] - a[0]) * s[1] - (c[1] - a[1]) * s[0]) / denominator;
  const u = ((c[0] - a[0]) * r[1] - (c[1] - a[1]) * r[0]) / denominator;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1 ? { t, u } : null;
}
// the nearest point on a segment, and how far along it is
function closest(p: MapPoint, a: MapPoint, b: MapPoint) {
  const dx = b[0] - a[0],
    dz = b[1] - a[1];
  const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dz) / (dx * dx + dz * dz || 1)));
  return { t, point: [a[0] + dx * t, a[1] + dz * t] as MapPoint };
}

export function buildRoadGraph(streets: [MapPoint, MapPoint][]): RoadGraph {
  // every street cut at its ends and wherever another street crosses it
  const cuts = streets.map(() => new Set<number>([0, 1]));
  streets.forEach(([a, b], i) =>
    streets.forEach(([c, d], j) => {
      if (j <= i) return;
      const hit = crossing(a, b, c, d);
      if (!hit) return;
      cuts[i]!.add(hit.t);
      cuts[j]!.add(hit.u);
    }),
  );
  const nodes: MapPoint[] = [];
  const edges: Edge[][] = [];
  const node = (point: MapPoint) => {
    const found = nodes.findIndex((other) => distance(other, point) < 0.5);
    if (found >= 0) return found;
    nodes.push(point);
    edges.push([]);
    return nodes.length - 1;
  };
  const link = (i: number, j: number) => {
    if (i === j || edges[i]!.some((edge) => edge.to === j)) return;
    const length = distance(nodes[i]!, nodes[j]!);
    edges[i]!.push({ to: j, length });
    edges[j]!.push({ to: i, length });
  };
  streets.forEach(([a, b], i) => {
    const points = [...cuts[i]!]
      .sort((x, y) => x - y)
      .map((t) => node([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]));
    for (let k = 1; k < points.length; k += 1) link(points[k - 1]!, points[k]!);
  });
  // join the separate bits of street where they come closest (until it is all one network)
  for (;;) {
    const group = new Array<number>(nodes.length).fill(-1);
    let groups = 0;
    for (let start = 0; start < nodes.length; start += 1) {
      if (group[start] !== -1) continue;
      const queue = [start];
      group[start] = groups;
      for (let head = 0; head < queue.length; head += 1)
        for (const edge of edges[queue[head]!]!)
          if (group[edge.to] === -1) {
            group[edge.to] = groups;
            queue.push(edge.to);
          }
      groups += 1;
    }
    if (groups <= 1) break;
    let best: [number, number] | null = null,
      bestLength = Infinity;
    for (let i = 0; i < nodes.length; i += 1)
      for (let j = 0; j < nodes.length; j += 1)
        if (group[i] === 0 && group[j] !== 0 && distance(nodes[i]!, nodes[j]!) < bestLength) {
          bestLength = distance(nodes[i]!, nodes[j]!);
          best = [i, j];
        }
    if (!best) break;
    link(...best);
  }
  return { nodes, edges };
}

// the way from one point to another: off to the nearest street, along the streets, then on to the place
export function findRoute(graph: RoadGraph, from: MapPoint, to: MapPoint): MapPoint[] {
  if (!graph.nodes.length) return [from, to];
  // join a point to the street network at the nearest point of the nearest street
  const join = (point: MapPoint) => {
    let best = { i: 0, j: 0, t: 0, point: graph.nodes[0]!, gap: Infinity };
    graph.edges.forEach((list, i) =>
      list.forEach((edge) => {
        if (edge.to < i) return;
        const near = closest(point, graph.nodes[i]!, graph.nodes[edge.to]!);
        const gap = distance(point, near.point);
        if (gap < best.gap) best = { i, j: edge.to, t: near.t, point: near.point, gap };
      }),
    );
    return best;
  };
  const start = join(from),
    end = join(to);
  // shortest way between the two joining points (Dijkstra over the street nodes, plus the two of them)
  const count = graph.nodes.length;
  const START = count,
    END = count + 1;
  const points = [...graph.nodes, start.point, end.point];
  const neighbours = (n: number): Edge[] => {
    const list: Edge[] = n < count ? [...graph.edges[n]!] : [];
    const attach = (joined: typeof start, id: number) => {
      if (n === id)
        list.push(...[joined.i, joined.j].map((to) => ({ to, length: distance(joined.point, points[to]!) })));
      else if (n === joined.i || n === joined.j) list.push({ to: id, length: distance(points[n]!, joined.point) });
    };
    attach(start, START);
    attach(end, END);
    // both on the same street: straight along it
    if (start.i === end.i && start.j === end.j && (n === START || n === END))
      list.push({ to: n === START ? END : START, length: distance(start.point, end.point) });
    return list;
  };
  const best = new Array<number>(count + 2).fill(Infinity);
  const came = new Array<number>(count + 2).fill(-1);
  const done = new Array<boolean>(count + 2).fill(false);
  best[START] = 0;
  for (;;) {
    let current = -1;
    for (let n = 0; n < count + 2; n += 1)
      if (!done[n] && best[n]! < Infinity && (current < 0 || best[n]! < best[current]!)) current = n;
    if (current < 0 || current === END) break;
    done[current] = true;
    for (const edge of neighbours(current))
      if (best[current]! + edge.length < best[edge.to]!) {
        best[edge.to] = best[current]! + edge.length;
        came[edge.to] = current;
      }
  }
  if (best[END] === Infinity) return [from, to];
  const way: MapPoint[] = [];
  for (let n = END; n !== -1; n = came[n]!) way.unshift(points[n]!);
  return [from, ...way, to].filter((point, i, all) => i === 0 || distance(point, all[i - 1]!) > 0.05);
}

// along a route: the point (and the way it heads) a fraction of the way along it
export function alongRoute(route: MapPoint[], fraction: number) {
  const lengths = route.slice(1).map((point, i) => distance(route[i]!, point));
  const total = lengths.reduce((sum, length) => sum + length, 0) || 1;
  let left = Math.max(0, Math.min(1, fraction)) * total;
  for (let i = 0; i < lengths.length; i += 1) {
    const length = lengths[i]!;
    if (left <= length || i === lengths.length - 1) {
      const a = route[i]!,
        b = route[i + 1]!;
      const t = length ? Math.min(1, left / length) : 1;
      return {
        point: [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t] as MapPoint,
        heading: Math.atan2(b[0] - a[0], b[1] - a[1]),
      };
    }
    left -= length;
  }
  return { point: route.at(-1)!, heading: 0 };
}
export const routeLength = (route: MapPoint[]) =>
  route.slice(1).reduce((sum, point, i) => sum + distance(route[i]!, point), 0);
